import { memo, useEffect, useRef } from "react";
import { CURVE_SAMPLES, sampleProfile } from "./velocity-curve.js";
import { repoName } from "../../lib/format.js";

export interface StreamLane {
  projectPath: string;
  /** Code volume per week — the stream's thickness. */
  volume: number[];
  /** MR count per week — the stream's particle density. */
  count: number[];
}

const DRAW_MS = 900;

function easeOutExpo(t: number) {
  return t >= 1 ? 1 : 1 - Math.pow(2, -10 * t);
}

function withAlpha(hex: string, alpha: number): string {
  const m = /^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(hex.trim());
  if (!m) return hex;
  const [r, g, b] = [m[1], m[2], m[3]].map((c) => Number.parseInt(c!, 16));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * The readable layer of the stream field: one ribbon per project, its thickness
 * the code volume of that week, all ribbons on one shared scale so their widths
 * compare directly. Drawn symmetrically around each lane's centre line and
 * revealed left to right once on mount.
 *
 * This stands alone — `VelocityStreamFlow` only adds particles on top of it.
 */
function VelocityStreamsImpl({
  lanes,
  laneHeight,
  maxVolume,
  laneColors,
}: {
  lanes: StreamLane[];
  laneHeight: number;
  maxVolume: number;
  /** One color per lane, fastest first — rank is carried by tint, not by hue. */
  laneColors: string[];
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const height = laneHeight * lanes.length;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reducedMotion =
      typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;

    const profiles = lanes.map((lane) => sampleProfile(lane.volume, maxVolume));
    let width = 0;
    let dpr = 1;
    let raf: number | undefined;
    let start = 0;

    function resize() {
      width = canvas!.clientWidth;
      dpr = Math.max(1, window.devicePixelRatio || 1);
      canvas!.width = Math.round(width * dpr);
      canvas!.height = Math.round(height * dpr);
    }

    // A lane never fills its full row: the gap is what keeps five ribbons
    // readable as five things rather than one stack.
    const LANE_PAD = 5;
    const halfMax = laneHeight / 2 - LANE_PAD;
    /** A hairline stays visible for a week with no movement at all. */
    const MIN_HALF = 0.75;

    function draw(now: number) {
      if (!start) start = now;
      const t = reducedMotion ? 1 : Math.min(1, (now - start) / DRAW_MS);
      const eased = easeOutExpo(t);

      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx!.clearRect(0, 0, width, height);
      if (width === 0) return;

      ctx!.save();
      ctx!.beginPath();
      ctx!.rect(0, 0, width * eased, height);
      ctx!.clip();

      profiles.forEach((profile, laneIndex) => {
        const centerY = laneIndex * laneHeight + laneHeight / 2;
        const color = laneColors[laneIndex] ?? laneColors[laneColors.length - 1]!;

        ctx!.beginPath();
        // Top edge left to right, then the bottom edge back — one closed ribbon.
        for (let s = 0; s < CURVE_SAMPLES; s += 1) {
          const x = (s / (CURVE_SAMPLES - 1)) * width;
          const half = Math.max(MIN_HALF, profile[s]! * halfMax);
          if (s === 0) ctx!.moveTo(x, centerY - half);
          else ctx!.lineTo(x, centerY - half);
        }
        for (let s = CURVE_SAMPLES - 1; s >= 0; s -= 1) {
          const x = (s / (CURVE_SAMPLES - 1)) * width;
          const half = Math.max(MIN_HALF, profile[s]! * halfMax);
          ctx!.lineTo(x, centerY + half);
        }
        ctx!.closePath();

        ctx!.fillStyle = withAlpha(color, 0.22);
        ctx!.fill();
        ctx!.strokeStyle = color;
        ctx!.lineWidth = 1.5;
        ctx!.lineJoin = "round";
        ctx!.stroke();
      });
      ctx!.restore();
    }

    resize();
    raf = requestAnimationFrame(function loop(now) {
      draw(now);
      if (!start || now - start < DRAW_MS) raf = requestAnimationFrame(loop);
    });
    const ro = new ResizeObserver(() => {
      resize();
      if (raf) cancelAnimationFrame(raf);
      start = 0;
      raf = requestAnimationFrame(function loop(now) {
        draw(now);
        if (!start || now - start < DRAW_MS) raf = requestAnimationFrame(loop);
      });
    });
    ro.observe(canvas);

    return () => {
      ro.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [lanes, laneHeight, maxVolume, laneColors, height]);

  const description = lanes
    .map((lane) => {
      const weeks = lane.volume.length;
      return `${repoName(lane.projectPath)}: ${lane.volume[weeks - 1] ?? 0} geänderte Zeilen diese Woche`;
    })
    .join("; ");

  return <canvas ref={canvasRef} className="block h-full w-full" role="img" aria-label={description} />;
}

/** Memoized: the tooltip above re-renders on every mouse move, and this layer
 *  must not rebuild its canvas when only the cursor moved. */
export const VelocityStreams = memo(VelocityStreamsImpl);
